/**
 * Cart Enhancements
 *
 * 1. ShippingBar  — updates the free-shipping progress bar whenever the cart
 *                   changes (listens for the theme's `cart:updated` event).
 *
 * 2. CartUpsell   — handles AJAX add-to-cart for the add-on product carousel
 *                   rendered by snippets/cart-upsell.liquid.
 *
 * Relies on globals already set by the Impulse theme:
 *   theme.routes.cartAdd       — Shopify cart add URL
 *   theme.cart.getCart()       — returns Promise<cartJSON>
 *   theme.Currency.formatMoney — money formatter
 *   theme.settings.moneyFormat — active money format string
 *
 * Loading: include this file in layout/theme.liquid after theme.js, e.g.:
 *   <script src="{{ 'cart-enhancements.js' | asset_url }}" defer></script>
 * Do NOT load it from a snippet — script tags injected via innerHTML
 * (AJAX cart rebuilds) are silently ignored by the browser.
 */

(function () {
  'use strict';

  /* ============================================================
     ShippingBar
  ============================================================ */
  var ShippingBar = {
    bar: null,
    fill: null,
    message: null,
    threshold: 0,

    init: function () {
      this.bar = document.querySelector('[data-shipping-bar]');
      if (!this.bar) return;

      this.threshold = parseInt(this.bar.dataset.threshold, 10);
      this.fill = this.bar.querySelector('[data-shipping-bar-fill]');
      this.message = this.bar.querySelector('[data-shipping-bar-message]');

      // Quantity changes inside the drawer — cart data included in event detail
      document.addEventListener('cart:updated', this.onCartUpdated.bind(this));

      // Add-to-cart from product pages / quick-add — bubbles up to document
      document.addEventListener('ajaxProduct:added', this.onCartBuild.bind(this));
    },

    onCartUpdated: function (evt) {
      if (!evt.detail || !evt.detail.cart) return;
      this.update(evt.detail.cart);
    },

    onCartBuild: function () {
      var self = this;
      theme.cart.getCart().then(function (cart) {
        self.update(cart);
      });
    },

    update: function (cart) {
      this.bar.hidden = cart.item_count === 0;
      if (cart.item_count === 0) return;

      var totalCents = cart.total_price;
      var remaining  = this.threshold - totalCents;
      var percent = Math.min(
        Math.round((totalCents / this.threshold) * 100),
        100
      );

      this.fill.style.width = percent + '%';

      if (remaining <= 0) {
        this.message.innerHTML = "You've unlocked free shipping!";
        this.bar.classList.add('cart-shipping-bar--unlocked');
      } else {
        var amount = theme.Currency.formatMoney(
          remaining,
          theme.settings.moneyFormat
        );
        this.message.innerHTML =
          'Spend <strong>' + amount + '</strong> more for free shipping';
        this.bar.classList.remove('cart-shipping-bar--unlocked');
      }
    }
  };

  /* ============================================================
     CartUpsell
  ============================================================ */
  var CartUpsell = {
    container: null,

    init: function () {
      this.container = document.querySelector('[data-cart-upsell]');
      if (!this.container) return;

      this.container.addEventListener('click', this.onClick.bind(this));
    },

    onClick: function (evt) {
      var btn = evt.target.closest('[data-upsell-add]');
      if (!btn || btn.disabled) return;

      evt.preventDefault();

      var variantId = parseInt(btn.dataset.variantId, 10);
      if (!variantId) return;

      this.addToCart(btn, variantId);
    },

    addToCart: function (btn, variantId) {
      btn.classList.add('btn--loading');
      btn.disabled = true;

      // theme.routes.cartAdd is already '/cart/add.js' — use as-is
      var cartAddUrl =
        (window.theme && theme.routes && theme.routes.cartAdd) ||
        '/cart/add.js';

      fetch(cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (res) {
          return res.json();
        })
        .then(
          function (data) {
            btn.classList.remove('btn--loading');
            btn.disabled = false;

            // Shopify returns status 422 for inventory errors
            if (data.status === 422 || data.status === 404) {
              var msg = data.description || data.message || 'Unavailable';
              alert(msg);
              return;
            }

            // 1. Rebuild the cart items list (uses existing theme event)
            document.dispatchEvent(new CustomEvent('cart:build'));

            // 2. Fetch updated cart totals so the shipping bar refreshes
            theme.cart.getCart().then(function (cart) {
              document.dispatchEvent(
                new CustomEvent('cart:updated', { detail: { cart: cart } })
              );
            });

            // 3. Button feedback
            var originalLabel =
              btn.dataset.upsellLabel || btn.textContent.trim();
            btn.dataset.upsellLabel = originalLabel;
            btn.textContent = 'Added!';
            btn.classList.add('btn--added');

            setTimeout(function () {
              btn.textContent = originalLabel;
              btn.classList.remove('btn--added');
            }, 2000);
          }.bind(this)
        )
        .catch(function (err) {
          btn.classList.remove('btn--loading');
          btn.disabled = false;
          console.error('[CartUpsell] Add to cart error:', err);
        });
    }
  };

  /* ============================================================
     UpsellCarousel
     CSS owns all sizing (scroll-snap + flex-basis percentages).
     JS arrows call scrollBy() one item step — width read live
     from the DOM, gap read from computed styles.
  ============================================================ */
  var UpsellCarousel = {
    container: null,
    track: null,
    items: null,
    prevBtn: null,
    nextBtn: null,

    init: function () {
      this.container = document.querySelector('[data-cart-upsell]');
      if (!this.container) return;

      this.track   = this.container.querySelector('[data-upsell-track]');
      this.items   = Array.prototype.slice.call(this.container.querySelectorAll('[data-upsell-item]'));
      this.prevBtn = this.container.querySelector('[data-upsell-prev]');
      this.nextBtn = this.container.querySelector('[data-upsell-next]');

      if (!this.items.length || !this.track) return;

      if (this.items.length === 1) {
        var nav = this.container.querySelector('[data-upsell-nav]');
        if (nav) nav.hidden = true;
        return;
      }

      this.prevBtn.addEventListener('click', this.prev.bind(this));
      this.nextBtn.addEventListener('click', this.next.bind(this));
      this.track.addEventListener('scroll', this.updateButtons.bind(this));

      // Drawer is closed at init time — scrollWidth/clientWidth are 0 so
      // updateButtons() here would wrongly disable next. Re-run once the
      // drawer is open and the layout has been painted.
      document.addEventListener('drawerOpen.CartDrawer', this.updateButtons.bind(this));
    },

    updateButtons: function () {
      var atStart = this.track.scrollLeft <= 1;
      var atEnd   = this.track.scrollLeft >= this.track.scrollWidth - this.track.clientWidth - 1;
      if (this.prevBtn) this.prevBtn.disabled = atStart;
      if (this.nextBtn) this.nextBtn.disabled = atEnd;
    },

    scrollStep: function () {
      var gap = parseInt(getComputedStyle(this.track).columnGap, 10) || 0;
      return this.items[0].offsetWidth + gap;
    },

    prev: function () { this.track.scrollBy({ left: -this.scrollStep(), behavior: 'smooth' }); },
    next: function () { this.track.scrollBy({ left:  this.scrollStep(), behavior: 'smooth' }); }
  };

  /* ============================================================
     Init
  ============================================================ */
  function init() {
    ShippingBar.init();
    CartUpsell.init();
    UpsellCarousel.init();
  }

  // theme.js dispatches `page:loaded` before our deferred script executes,
  // so we'd miss the event if we only listen for it. Instead, check if the
  // theme has already initialized (routes exist) and init immediately;
  // otherwise fall back to the event for edge cases (e.g. SPAs).
  if (window.theme && theme.routes) {
    init();
  } else {
    document.addEventListener('page:loaded', init);
  }
})();
